import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@evergis/ui";
import { ProjectContentItemDc } from "@evergis/api";

import { Alias, LegendToggler, Paragraph, ServiceDescr, ServicePath } from "../styled";
import { getTableAlias } from "../../../evergis/core/table/getTableInfo";
import { isErrorStatus, LayerFetchStatus } from "../../../resources/layerWithTable";
import { useProjectLayerVisibility } from "../../../hooks/project/useProjectLayerProp";
import { useResetFeatureCard } from "../../FeatureCard/hooks/useResetFeatureCard";
import { LayerInfo, TableInfo } from "../../../interfaces/resourceInfo";

interface ItemTitleProps {
  path?: string[];
  isLegendExpanded?: boolean;
  toggleLegend?: VoidFunction;
  projectLayer?: ProjectContentItemDc;
  halfVisible?: boolean;
  hasClassification?: boolean;
  noControl?: boolean;
  noLegend?: boolean;
  noVisibilityClick?: boolean;
  layer: LayerInfo;
  status?: LayerFetchStatus;
  table: TableInfo;
}

const MAX_PATH_LENGTH = 40;

export const ItemTitle: FC<ItemTitleProps> = ({
  isLegendExpanded,
  toggleLegend,
  layer,
  projectLayer,
  path,
  table,
  status,
  halfVisible,
  hasClassification,
  noControl,
  noLegend,
  noVisibilityClick,
}) => {
  const { t } = useTranslation("layerList");

  const getItemDescription = useCallback(() => {
    switch (status) {
      case "layerError":
        return t("layerNotAvailable");
      case "tableError":
        return t("dataNotAvailable");
      default:
        return getTableAlias(table);
    }
  }, [status, t, table]);

  const itemAlias = (layer && layer.alias) || layer?.name;
  const itemDescription = getItemDescription();
  const isError = isErrorStatus(status);

  const renderPath = useMemo(() => {
    if (!path?.length) return null;

    const fullPath = path.join(" / ");
    const shortPath = fullPath.length >= MAX_PATH_LENGTH ? ["...", path.pop()].join(" / ") : fullPath;

    return (
      <Tooltip content={fullPath} placement="top" arrow>
        {ref => <ServicePath ref={ref}>{shortPath}</ServicePath>}
      </Tooltip>
    );
  }, [path]);

  const [, toggleVisibilityLayer] = useProjectLayerVisibility(projectLayer);
  const toggleVisibility = useResetFeatureCard(toggleVisibilityLayer, [toggleVisibilityLayer], layer.name);

  return (
    <Paragraph>
      <Alias
        withError={isError}
        title={itemAlias}
        halfVisible={halfVisible}
        onClick={noControl && !noVisibilityClick ? toggleVisibility : undefined}
      >
        {itemAlias}
      </Alias>
      {isError && (
        <ServiceDescr title={itemDescription} withError halfVisible={halfVisible}>
          {itemDescription}
        </ServiceDescr>
      )}
      {renderPath}
      {!noLegend && hasClassification && !isError && (
        <LegendToggler onClick={toggleLegend} title={isLegendExpanded ? null : t("legend")} toggled={isLegendExpanded}>
          {isLegendExpanded ? t("hideLegend") : t("showLegend")}
        </LegendToggler>
      )}
    </Paragraph>
  );
};
